/**
 * Helpers for the free-form `additionalData` of an entry.
 *
 * Besides the values typed into the contest/style additional fields, the map may
 * hold lists of author objects added by the registration flow (`authors`). Such
 * values must never be printed with `String(value)` - that renders "[object Object]".
 */

export interface AdditionalFieldDefinition {
  key?: string | null;
  fieldType?: string | null;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (source: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = source[key];

    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }

  return '';
};

const formatAuthorObject = (author: Record<string, unknown>) => {
  const fullName = [
    readString(author, 'firstName', 'first_name'),
    readString(author, 'lastName', 'last_name'),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    fullName ||
    readString(author, 'name', 'fullName', 'full_name') ||
    readString(author, 'email')
  );
};

/**
 * Renders any additional data value as plain text: a list of author objects becomes
 * "First Last, First Last", scalars are stringified, `null`/`undefined` become ''.
 */
export const formatAdditionalDataValue = (value: unknown): string => {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(formatAdditionalDataValue).filter(Boolean).join(', ');
  }

  if (isPlainObject(value)) {
    return formatAuthorObject(value) || JSON.stringify(value);
  }

  return String(value);
};

/**
 * Prepares stored additional data for editing in plain inputs: values of configured
 * (non-boolean) fields that are not scalars - e.g. a structured authors list that
 * ended up under a text field - are turned into readable text, so the input does not
 * show (and later persist) "[object Object]". Everything else is left untouched.
 */
export const normalizeAdditionalDataForEditing = <
  T extends Record<string, unknown>,
>(
  additionalData: T | null | undefined,
  fields: AdditionalFieldDefinition[],
): T => {
  const data = { ...(additionalData || {}) } as Record<string, unknown>;

  fields.forEach((field) => {
    const key = field?.key;

    if (!key || field.fieldType === 'boolean') {
      return;
    }

    const value = data[key];

    if (value !== null && typeof value === 'object') {
      data[key] = formatAdditionalDataValue(value);
    }
  });

  return data as T;
};
